import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import DripAssignmentCard from '@/components/DripAssignmentCard';
import { TasksPanel } from '@/components/tasks/TasksPanel';
import { BuyerTagsEditor } from '@/components/buyers/BuyerExtras';
import { ProfileCard } from '@/components/buyersV2/ProfileCard';
import { ContactsCard } from '@/components/buyersV2/ContactsCard';
import { PurchasesCard } from '@/components/buyersV2/PurchasesCard';
import { ActivityCard, MatchedLeadsCard, BuyerCampaignsCard } from '@/components/buyersV2/BuyerSideCards';
import { money, shortDate, TIER_STYLE } from '@/lib/buyersV2';

function Stat({ label, value, sub, testid }) {
  return (
    <div className="p-3 rounded-lg bg-white border border-gray-200">
      <p className="text-[10px] uppercase tracking-wider text-gray-400">{label}</p>
      <p data-testid={testid} className="text-sm font-semibold text-gray-900">{value}</p>
      {sub && <p className="text-[11px] text-gray-400 truncate">{sub}</p>}
    </div>
  );
}

export default function BuyerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [activityKey, setActivityKey] = useState(0);

  const load = useCallback(async () => {
    try { setB((await API.get(`/buyers/${id}`)).data); setActivityKey(k => k + 1); }
    catch { toast.error('Buyer not found'); navigate('/buyers'); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const saveTags = async (tags) => {
    const add = tags.filter(t => !(b.tags || []).includes(t));
    const remove = (b.tags || []).filter(t => !tags.includes(t));
    setB(p => ({ ...p, tags }));
    await API.post(`/buyers/${id}/tags`, { add, remove }).catch(() => toast.error('Failed to save tags'));
  };
  const del = async () => {
    if (!window.confirm('Delete this buyer with all contacts and purchases?')) return;
    await API.delete(`/buyers/${id}`); toast.success('Buyer deleted'); navigate('/buyers');
  };

  if (!b) return <div className="h-64 rounded-xl bg-gray-100 animate-pulse" />;

  return (
    <div data-testid="buyer-detail-page" className="space-y-5 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/buyers')} data-testid="back-to-buyers"><ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="buyer-entity-name">{b.entity_name || b.name}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {b.tier && <Badge variant="outline" className={TIER_STYLE[b.tier]} data-testid="buyer-tier">{b.tier}</Badge>}
              {b.buyer_score > 0 && <Badge variant="outline">Score {b.buyer_score}/5</Badge>}
              {b.strategy && <Badge variant="outline">{b.strategy}</Badge>}
              {b.county && <span className="text-xs text-gray-500">{b.county}</span>}
              {b.market_tab && <span className="text-xs text-gray-500 whitespace-nowrap">· {b.market_tab}</span>}
              <BuyerTagsEditor tags={b.tags || []} onChange={saveTags} />
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={del} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-buyer-btn"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Stat label="Buys 24mo / 12mo / 90d" value={`${b.total_purchases_24mo || 0} / ${b.purchases_12mo || 0} / ${b.purchases_90d || 0}`} testid="stat-buys" />
        <Stat label="Top Zip" value={b.top_zip || '—'} sub={b.top_zip_count ? `${b.top_zip_count} buys` : ''} testid="stat-top-zip" />
        <Stat label="Top City" value={b.top_city || '—'} sub={b.top_city_count ? `${b.top_city_count} buys` : ''} />
        <Stat label="Last Purchase" value={money(b.last_purchase_price)} sub={`${shortDate(b.last_purchase_date)} ${b.last_purchase_address || ''}`} testid="stat-last" />
        <Stat label="Price Range" value={b.min_price ? `${money(b.min_price)}–${money(b.max_price)}` : '—'} sub={b.avg_price ? `avg ${money(b.avg_price)}` : ''} />
        <Stat label="Typical" value={b.typical_beds ? `${Math.round(b.typical_beds)}bd/${Math.round(b.typical_baths)}ba` : '—'}
          sub={[b.typical_sqft ? `${b.typical_sqft} sqft` : '', (b.mf_kept_count || b.mf_flipped_count) ? `MF ${b.mf_kept_count || 0} kept/${b.mf_flipped_count || 0} flip` : '', b.active_listings_count ? `${b.active_listings_count} listed` : ''].filter(Boolean).join(' · ')} />
      </div>

      <ProfileCard buyer={b} onSaved={load} />
      <ContactsCard buyerId={id} onChanged={load} />
      <PurchasesCard buyer={b} onChanged={load} />
      <div className="grid lg:grid-cols-2 gap-5 [&>*]:min-w-0">
        <MatchedLeadsCard buyerId={id} />
        <BuyerCampaignsCard buyerId={id} />
      </div>
      <ActivityCard buyerId={id} refreshKey={activityKey} />
      <TasksPanel buyerId={id} />
      <DripAssignmentCard targetType="buyer" targetId={id} />
    </div>
  );
}
